import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/users';
    import { computed, ref, watch } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { onMounted } from 'vue';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import { useUrlSearch } from '@/composables/useUrlSearch';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateUser from '@/components/users/CreateUser.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { ColumnFiltersState, SortingState } from '@tanstack/vue-table';
    import type { SharedData, BreadcrumbItem, User } from '@/types';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'Users',
  setup(__props) {

    const { hasPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.administration',
        },
        {
            title: 'users.header',
            href: route('v2.users.index'),
        },
    ];

    const totalRecords = ref(0);
    const pagination = ref({
        pageIndex: 0,
        pageSize: 20,
    });
    const columnFilters = ref<ColumnFiltersState>([]);

    const { extractAndCleanSearch } = useUrlSearch();
    const globalFilter = ref(extractAndCleanSearch());
    const sorting = ref<SortingState>([]);

    const initialQueryDone = ref(false);

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.USERS, selectedCompanyId],
        queryFn: getUsers,
    });

    watch(
        () => isFetching.value,
        (newVal, oldVal) => {
            if (!initialQueryDone.value && oldVal === true && newVal === false) {
                initialQueryDone.value = true;
            }
        },
    );

    async function getUsers() {
        const { data } = await axios.get(route('v2.users.table', selectedCompanyId.value), {
            params: {
                length: pagination.value.pageSize,
                start: pagination.value.pageIndex * pagination.value.pageSize,
                search: globalFilter.value || undefined,
                filters: buildFilterParams(columnFilters.value),
                orderBy: sorting.value[0]?.id,
                orderDir: sorting.value[0]?.desc ? 'desc' : 'asc',
            },
        });
        totalRecords.value = data.recordsTotal;
        return data.data;
    }

    const handlePaginationChange = (newPagination: { pageIndex: number; pageSize: number }) => {
        pagination.value = newPagination;
        refetch();
    };

    const handleGlobalFilterChange = (value: string) => {
        if (!initialQueryDone.value) return;
        globalFilter.value = value;
        pagination.value.pageIndex = 0; // Reset to first page
        scheduleRefetch();
    };

    function buildFilterParams(filters: ColumnFiltersState) {
        const obj: Record<string, any> = {};
        for (const f of filters) {
            obj[f.id] = f.value;
        }
        return obj;
    }

    const dataTableRef = ref<any>(null);
    let searchDebounce: ReturnType<typeof setTimeout> | null = null;

    const scheduleRefetch = () => {
        if (searchDebounce) clearTimeout(searchDebounce);
        searchDebounce = setTimeout(() => {
            pagination.value.pageIndex = 0;
            refetch();
        }, 300);
    };

    watch(
        () => dataTableRef.value?.table?.getState().columnFilters,
        (filters) => {
            if (!initialQueryDone.value) return;
            if (!filters) return;
            columnFilters.value = filters as ColumnFiltersState;
            scheduleRefetch();
        },
        { deep: true },
    );

    watch(
        () => dataTableRef.value?.table?.getState().globalFilter,
        (value) => {
            if (!initialQueryDone.value) return;
            if (typeof value !== 'string') return;
            globalFilter.value = value;
            scheduleRefetch();
        },
    );

    watch(
        () => dataTableRef.value?.table?.getState().sorting,
        (value) => {
            if (!initialQueryDone.value) return;
            sorting.value = (value || []) as SortingState;
            scheduleRefetch();
        },
        { deep: true },
    );

    onMounted(() => {
        initialQueryDone.value = true;
    });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(Head), {
      title: _ctx.$t('profile.profile_settings')
    }, null, 8, ["title"]),
    _createVNode(AppLayout, { breadcrumbs: breadcrumbs }, {
      default: _withCtx(() => [
        (_unref(hasPermission)(_unref(Permission).USERS_VIEW))
          ? (_openBlock(), _createBlock(PageCard, { key: 0 }, {
              default: _withCtx(() => [
                _createVNode(DataTable, {
                  ref_key: "dataTableRef",
                  ref: dataTableRef,
                  "table-key": _unref(QueryKey).USERS,
                  columns: _unref(columns),
                  data: _unref(data),
                  loading: _unref(isPending),
                  "manual-pagination": true,
                  "manual-sorting": true,
                  "manual-filtering": true,
                  "total-records": totalRecords.value,
                  "default-pagination": pagination.value,
                  "auto-url-search": true,
                  "default-column-visibility": {
                    id: false,
                },
                  onGlobalFilterChange: handleGlobalFilterChange,
                  "default-sorting": [
                    {
                        id: 'name',
                        desc: false,
                    },
                ],
                  onPaginationChange: handlePaginationChange
                }, {
                  title: _withCtx(() => [
                    _createVNode(_unref(CardTitle), null, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_ctx.$t('users.page_header')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  actions: _withCtx(() => [
                    _createVNode(DataTableToolbarActions, { name: "users" }, {
                      default: _withCtx(() => [
                        (_unref(hasPermission)(_unref(Permission).USERS_CREATE))
                          ? (_openBlock(), _createBlock(CreateUser, { key: 0 }))
                          : _createCommentVNode("", true)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }, 8, ["table-key", "columns", "data", "loading", "total-records", "default-pagination"])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})